<template>

    <div class="order-item-index">

        <div class="flex">
            <div class="flex-1 mb-4">
                <h1 class="text-2xl font-medium text-gray-900 capitalize">Order Items</h1>
            </div>

            <div class="flex mb-4 justify-end">
                <router-link :to="{ name: 'place-order' }" class="btn">Place Order</router-link>
            </div>
        </div>

        <OrderIndexFilterBar v-model="indexModel" @submitFilter="indexModel.load()" @resetFilters="indexModel.resetFilters()"></OrderIndexFilterBar>

        <div class="split-index-table"  v-if="indexModel.isLoaded()">
            <div>
                <div class="index-table" v-if="indexModel.hasResults()">
                    <div>
                        <div>
                            <table>
                                <thead>
                                <tr class="flex">
                                    <th>
                                        &nbsp;
                                    </th>
                                    <th scope="col" class="w-3/6 p-3 text-left tracking-wider" :class="{ 'lg:w-4/12': !indexModel.hasSelectedItem() }">
                                        Product
                                    </th>
                                    <th scope="col" class="w-1/6 p-3 text-center tracking-wider" :class="{ 'lg:w-1/12': !indexModel.hasSelectedItem() }">
                                        Status
                                    </th>
                                    <th scope="col" class="w-2/6 text-right lg:text-center relative p-3" :class="{ 'lg:w-3/12': !indexModel.hasSelectedItem() }">
                                        Gross / Net
                                    </th>
                                    <th scope="col" class="hidden lg:table-cell text-center relative p-3" :class="{ 'lg:hidden': indexModel.hasSelectedItem(), 'w-1/12': !indexModel.hasSelectedItem() }">
                                        Discount / Fee
                                    </th>
                                    <th scope="col" class="hidden lg:table-cell text-right relative p-3" :class="{ 'lg:hidden': indexModel.hasSelectedItem(), 'w-3/12': !indexModel.hasSelectedItem() }">
                                        Created
                                    </th>
                                </tr>
                                </thead>
                                <tbody class="flex flex-col justify-between overflow-y-scroll w-full max-h-96 xl:max-h-120" :class="{ 'border-r': indexModel.hasSelectedItem() }">
                                    <tr class="flex px-2 colored-row cursor-pointer" v-for="(item, index) in indexModel.getData()" @click.prevent="indexModel.selectIndexItem(index)">
                                        <td>
                                            <img :src="item.product_image" alt="No Image" class="w-14 bg-contain my-2">
                                        </td>
                                        <td class="w-3/6" :class="{ 'lg:w-4/12': !indexModel.hasSelectedItem() }">
                                            <span class="font-medium" :class="{ 'text-indigo-500': indexModel.selected === index }">{{ item.product }}</span><br>
                                            <span class="text-xs text-gray-500">{{ item.reference }}</span>
                                        </td>
                                        <td class="w-1/6 text-center items-center" :class="{ 'lg:w-1/12': !indexModel.hasSelectedItem() }">
                                            <span :class="`status-box ${statusBox(item.status)}`">
                                                {{ convertStatus(item.status) }}
                                            </span>
                                        </td>
                                        <td class="w-2/6 text-right lg:text-center" :class="{ 'lg:w-3/12': !indexModel.hasSelectedItem() }">
                                            <span class="text-gray-700 font-medium">{{ formatCurrency(item.currency, item.gross_value / 100) }}</span><br>
                                            <span class="text-xs text-gray-500">{{formatCurrency(item.currency, item.net_value / 100) }}</span>
                                        </td>
                                        <td class="hidden lg:table-cell text-center" :class="{ 'lg:hidden': indexModel.hasSelectedItem(), 'w-1/12': !indexModel.hasSelectedItem() }">
                                            <span class="text-gray-700 font-medium">{{ (item.discount / 100) }}%</span><br>
                                            <span class="text-xs text-gray-500">{{formatCurrency(item.currency, item.transaction_fee / 100) }}</span>
                                        </td>
                                        <td class="hidden lg:table-cell text-right" :class="{ 'lg:hidden': indexModel.hasSelectedItem(), 'w-3/12': !indexModel.hasSelectedItem() }">
                                            <span class="block">{{ formatDate(item.updated_at) }}</span>
                                            <span class="text-xs">Created by </span> <span class="text-xs text-gray-500 font-medium">{{ item.account_name }}</span>
                                        </td>
                                    </tr>
                                    <tr class="py-6 text-xs font-medium">
                                        <infinite-loading @infinite="infiniteHandler">
                                            <div slot="spinner">Loading...</div>
                                            <div slot="no-more">No more Orders</div>
                                        </infinite-loading>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div class="no-results panel" v-else>
                    <div class="p-10">
                        <div class="text-gray-200 my-6">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>

                        <h2 class="text-gray-600 font-medium text-2xl text-center">No Orders Available</h2>

                         <p class="text-center mt-4 text-sm text-gray-500">
                            We could not find any orders, <router-link :to="{ name: 'place-order' }">create an order</router-link>.
                        </p>
                    </div>
                </div>

                <!-- start: selected order item popout -->
                <div class="index-table-preview max-h-96 xl:max-h-132" v-if="indexModel.hasSelectedItem() && indexModel.hasResults()">
                    <div v-if="indexModel.hasSelectedItem()">
                        <div class="flex mb-2">
                            <h2 class="flex-1 items-center font-medium text-2xl">
                                {{ indexModel.selected.reference }}
                            </h2>

                            <router-link :to="{ name: 'order.show', params: { reference: indexModel.selected.reference } }" custom v-slot="{ href, route, navigate, isActive, isExactActive }">
                                <button class="btn-outline text-xs" @click="navigate">
                                    View Order
                                </button>
                            </router-link>
                        </div>

                        <table class="w-full text-sm mb-4">
                            <tbody>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Item ID</td>
                                <td class="w-4/6">
                                    <span class="text-gray-500">{{ indexModel.selected.uuid }}</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Status</td>
                                <td class="w-4/6">
                                    <span :class="`status-box ${statusBox(indexModel.selected.status)}`">
                                        {{ convertStatus(indexModel.selected.status) }}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">User</td>
                                <td class="w-4/6">
                                    <span v-if="indexModel.selected.user_name">
                                        {{ indexModel.selected.user_name }} &lt;<a :href="`mailto:${indexModel.selected.user_email}`" target="_blank" class="text-brand-700">{{ indexModel.selected.user_email }}</a>&gt;
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Order Type</td>
                                <td class="w-4/6 uppercase">
                                    <span class="status-box">
                                        {{ indexModel.selected.order_type }}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Date</td>
                                <td class="w-4/6">
                                    {{ formatDate(indexModel.selected.updated_at) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">
                                    Invoice
                                </td>
                                <td class="w-4/6">
                                    <a :href="`/app/download/invoice/${indexModel.selected.invoice}`" v-if="indexModel.selected.invoice">
                                        {{ indexModel.selected.invoice }}
                                    </a>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">
                                    Code
                                </td>
                                <td class="w-4/6">
                                    <span v-if="indexModel.selected.masked_code">{{ indexModel.selected.masked_code }}</span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">
                                    Serial
                                </td>
                                <td class="w-4/6">
                                    <span v-if="indexModel.selected.item_value && indexModel.selected.item_value.serial">{{ indexModel.selected.item_value.serial }}</span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">
                                    Processor Reference
                                </td>
                                <td class="w-4/6">
                                    <span v-if="indexModel.selected.processor_reference">{{ indexModel.selected.processor_reference }}</span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">
                                    Processor Job ID
                                </td>
                                <td class="w-4/6">
                                    <span v-if="indexModel.selected.processor_job_id">{{ indexModel.selected.processor_job_id }}</span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        <hr class="mb-4 border-gray-300">

                        <table class="text-sm mb-4 w-full">
                            <tbody>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Gross Value</td>
                                <td class="w-4/6">
                                    {{ formatCurrency(indexModel.selected.currency, indexModel.selected.gross_value / 100) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Transaction Fee</td>
                                <td class="w-4/6">
                                    {{ formatCurrency(indexModel.selected.currency, indexModel.selected.transaction_fee / 100) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Tax</td>
                                <td class="w-4/6">
                                    {{ formatCurrency(indexModel.selected.currency, indexModel.selected.tax / 100) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Transaction Fee Tax</td>
                                <td class="w-4/6">
                                    {{ formatCurrency(indexModel.selected.currency, indexModel.selected.fee_tax / 100) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Payment Type</td>
                                <td class="w-4/6">
                                    <span class="status-box">
                                        {{ indexModel.selected.payment_type }}
                                    </span>
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        <hr class="mb-4 border-gray-300">

                        <div class="flex mb-4">
                            <div class="w-2/3">
                                <h3 class="font-medium mb-2">{{ indexModel.selected.product }}</h3>

                                <table class="text-sm mb-4 w-full">
                                    <tbody>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">Discount</td>
                                        <td class="w-4/6 font-medium">
                                            <span class="text-base">{{ (indexModel.selected.discount / 100) }}</span>%
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">Product Value</td>
                                        <td class="w-4/6">
                                            {{ formatCurrency(indexModel.selected.currency, indexModel.selected.product_gross / 100) }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">Product Cost</td>
                                        <td class="w-4/6">
                                            {{ formatCurrency(indexModel.selected.currency, indexModel.selected.product_net / 100) }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="w-1/3">
                                <img :src="indexModel.selected.product_image" :alt="indexModel.selected.product" class="object-contain w-full rounded">
                            </div>
                        </div>

                        <div v-if="indexModel.selected.order_status === 'complete'">
                            <hr class="mb-4 border-gray-300">

                            <div>
                                <table class="text-sm mb-4 w-full">
                                    <tbody>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">Serial</td>
                                        <td class="w-4/6">
                                            {{ indexModel.selected.item_value.serial ? indexModel.selected.item_value.serial : '-' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">Code</td>
                                        <td class="w-4/6">
                                            {{ indexModel.selected.item_value.code ? indexModel.selected.item_value.code : '-' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">PIN</td>
                                        <td class="w-4/6">
                                            {{ indexModel.selected.item_value.pin ? indexModel.selected.item_value.pin : '-' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">URL</td>
                                        <td class="w-4/6">
                                            <a :href="indexModel.selected.item_value.url" target="_blank" class="flex" v-if="indexModel.selected.item_value.url">
                                                Redemption Page
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </a>
                                            <span v-else>-</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">Activation</td>
                                        <td class="w-4/6">
                                            {{ indexModel.selected.item_value.activation ? formatDate(indexModel.selected.item_value.activation) : '-' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">Expiration</td>
                                        <td class="w-4/6">
                                            {{ indexModel.selected.item_value.expiration ? formatDate(indexModel.selected.item_value.expiration) : '-' }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-if="indexModel.selected.item_delivery && indexModel.selected.item_delivery.email_to_customer === true">
                            <hr class="mb-4 border-gray-300">
                            <div>
                                <table class="text-sm mb-4 w-full">
                                    <tbody>
                                    <tr>
                                        <td class="w-2/6 text-gray-400 py-1">Customer</td>
                                        <td class="w-4/6">
                                            {{ indexModel.selected.item_delivery.customer.name ? indexModel.selected.item_delivery.customer.name : '-' }} {{ indexModel.selected.item_delivery.customer.email ? '(' + indexModel.selected.item_delivery.customer.email + ')': '(-)' }}
                                        </td>
                                    </tr>
                                    <tr v-if="indexModel.selected.item_delivery.notify_customer_at">
                                        <td class="w-2/6 text-gray-400 py-1">Deliver at</td>
                                        <td class="w-4/6">
                                            {{ formatDate(indexModel.selected.item_delivery.notify_customer_at) }}
                                        </td>
                                    </tr>
                                    <tr v-if="indexModel.selected.item_delivery.gift_message">
                                        <td class="w-2/6 text-gray-400 py-1">Message</td>
                                        <td class="w-4/6">
                                            {{ indexModel.selected.item_delivery.gift_message }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div class="loading" v-else>
                        <Loading></Loading>
                    </div>
                </div>
                <!-- end: selected order item popout -->

            </div>
        </div>
        <Loading message="Loading Order Items" v-else></Loading>

    </div>

</template>

<script>
import Loading from "../Support/Loading";
import InfiniteLoading from "vue-infinite-loading";
import {OrderItemIndex} from "../../app/Order/OrderItemIndex";
import OrderIndexFilterBar from "./OrderIndexFilterBar";
import {FormattingMixin, HttpStatusMixin, PermissionMixin} from "../../support/mixins";

export default {
    name: "OrderItemIndex",
    mixins: [PermissionMixin, FormattingMixin, HttpStatusMixin],
    components: {
        OrderIndexFilterBar,
        InfiniteLoading,
        Loading
    },
    data() {
        return {
            indexModel: new OrderItemIndex
        }
    },
    methods: {
        infiniteHandler($state) {
            this.indexModel.infiniteLoadingHandler($state);
        },
    },
    mounted() {
        this.indexModel.load();
    }
}
</script>
